import { Constants } from '@adobe/aem-spa-page-model-manager';
import load from '@loadable/component';
import { useMemo } from 'react';

import { ExperienceFragment } from '@/components/ExperienceFragment/ExperienceFragment';
import { VpcBannerVariantContext } from '@/features/VpcBanner/VpcBannerVariantContext';
import { defaultValues } from '@/utils/defaultValues';

const FeatureProviderSearch = load(() => import('@/features/ProviderSearch'));

export class VpcBannerExperienceFragment extends ExperienceFragment {
    render() {
        return (
            <VpcBannerVariantContext.Provider value="embedded">
                {super.render()}
            </VpcBannerVariantContext.Provider>
        );
    }
}

const ProviderSearch = (props) => {
    const {
        noResultsFragment,
        searchServerErrorFragment,
        vpcBannerFragment,
        title,
        description,
        image,
        media,
        illustration,
        typeSpecialty,
        zipCode,
        zipCodeError,
        searchError,
        filterLabel,
        filterAriaLabel,
        resetAllLabel,
        resetAllAriaLabel,
        seeResultsLabel,
        seeResultsAriaLabel,
        hideMobileIllustration,
        initialChildren,
        resultsChildren,
        maps,
        patientTypeId,
        notAcceptingPatientsText,
        existingPatientText,
        noOnlineSchedulingText,
        availabilityLabel,
        enableTypeahead,
        typeAheadFixedItem,
        coveo,
        outsideMatchesDisabled,
        outsideMatchesBannerDescription,
        outsideMatchesResultsTitle,
        fewResultsMessage,
        boostRulesets,
    } = props;

    const noResults = useMemo(
        () =>
            noResultsFragment ? (
                <ExperienceFragment
                    cqItems={noResultsFragment[Constants.ITEMS_PROP]}
                    cqItemsOrder={noResultsFragment[Constants.ITEMS_ORDER_PROP]}
                    {...noResultsFragment}
                />
            ) : undefined,
        [noResultsFragment],
    );
    const error = useMemo(
        () =>
            searchServerErrorFragment ? (
                <ExperienceFragment
                    cqItems={searchServerErrorFragment[Constants.ITEMS_PROP]}
                    cqItemsOrder={searchServerErrorFragment[Constants.ITEMS_ORDER_PROP]}
                    {...searchServerErrorFragment}
                />
            ) : undefined,
        [searchServerErrorFragment],
    );
    const vpcBanner = useMemo(() => {
        if (!vpcBannerFragment) return undefined;
        const items = vpcBannerFragment[Constants.ITEMS_PROP];
        if (!items || Object.keys(items).length === 0) return undefined;
        return (
            <VpcBannerExperienceFragment
                cqItems={items}
                cqItemsOrder={vpcBannerFragment[Constants.ITEMS_ORDER_PROP]}
                {...vpcBannerFragment}
            />
        );
    }, [vpcBannerFragment]);

    return (
        <FeatureProviderSearch
            {...props}
            coveo={coveo}
            enableTypeahead={enableTypeahead}
            typeAheadFixedItem={typeAheadFixedItem}
            patientTypeId={patientTypeId}
            notAcceptingPatientsText={notAcceptingPatientsText}
            existingPatientText={existingPatientText}
            noOnlineSchedulingText={noOnlineSchedulingText}
            availabilityLabel={availabilityLabel}
            noResultsComponent={noResults}
            errorComponent={error}
            vpcBannerComponent={vpcBanner}
            initialChildren={initialChildren}
            resultsChildren={resultsChildren}
            title={title || 'Find a Location'}
            description={description}
            image={image}
            media={media}
            illustration={illustration}
            searchLabel={typeSpecialty || 'Search Type or Specialty'}
            zipLabel={zipCode || 'City or Zip'}
            zipErrorMessage={zipCodeError || 'Please enter a valid ZIP code'}
            searchErrorMessage={searchError || 'Please enter at least one search query'}
            hideMobileIllustration={hideMobileIllustration}
            filterLabel={filterLabel || 'Filter'}
            filterAriaLabel={filterAriaLabel}
            resetAllLabel={resetAllLabel || 'Reset'}
            resetAllAriaLabel={resetAllAriaLabel}
            seeResultsLabel={seeResultsLabel || 'See Results'}
            seeResultsAriaLabel={seeResultsAriaLabel}
            googleMaps={defaultValues(
                {
                    apiKey: 'AIzaSyDRdNprx4liInUfIU8gA6HpPL9p4tu8NYQ',
                    mapId: 'b936a6d7221f66ee',
                },
                maps,
            )}
            outsideMatchesDisabled={outsideMatchesDisabled}
            outsideMatchesBannerDescription={outsideMatchesBannerDescription}
            outsideMatchesResultsTitle={outsideMatchesResultsTitle}
            fewResultsMessage={fewResultsMessage}
            boostRulesets={boostRulesets}
        />
    );
};

export default ProviderSearch;
